import { Controller } from "@hotwired/stimulus"
import { destroy, post } from "@rails/request.js"
import { escapeHtml } from "../lib/html_sanitizer"
import {
  confirmUnlicensedStates,
  unlicensedStates,
} from "../lib/unlicensed_states_notice"
import { setHidden } from "../lib/display_toggle"
import { setValueOrEmpty, valueOrEmptyHtml } from "../lib/empty_value"
import { fieldToken, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { syncGateReason } from "../lib/gate_reason"
import { setButtonLoading } from "../lib/button_loading"
import { statusBadgeClass } from "../lib/status_badge"

// Same copy as LeadOrdersHelper#integration_choice_options: blank means "use my default".
const INTEGRATION_DEFAULT_LABEL = "Use my default for this lead type"
// Shown when the server answers with something other than JSON (a CSRF 422
// page, a 500 page) — never response.statusText or the raw body.
const UNEXPECTED_RESPONSE_MESSAGE = "The server returned an unexpected response. Please try again."
const REMOVE_ITEM_FALLBACK_MESSAGE = "Could not remove that item. Please try again."
// A 409 on remove whose body carries `cart_lock` is the cart refusing to release
// an order because Stripe says its payment completed, or could not confirm the
// checkout is over. The sentence says what happens next and may name a release
// time (support ticket 2778), so it is held on screen long enough to read and
// shown as a caution rather than a failure.
const CHECKOUT_LOCK_TOAST_MS = 12000

// A11Y-1 (#13177): every disabled control in the store explains itself. While
// no lead type + bucket is chosen, the quantity inputs and Add to Cart buttons
// point (aria-describedby) at the visible guided-steps hint the view renders
// under this id; once the filters are set, a still-disabled control names its
// own reason as a tooltip. The cart CTAs' reasons come from the view as values
// (AgedCartRail owns that copy) so the server first paint and the live update
// say the same thing.
const STEPS_HINT_ID = "aged-store-steps-hint"
const NO_LEADS_REASON = "No leads available in this state"
const QUANTITY_REASON = "Enter a quantity first"
// While an add-to-cart request is in flight every add button is disabled
// (#14207) and says so.
const IN_FLIGHT_REASON = "Adding to your cart…"
// Shown IN the availability cell (and as the quantity box placeholder) when a
// state has nothing in stock: the one reason a row is greyed out. Kept short so
// the cell stays one line beside the bar.
const OUT_OF_STOCK_LABEL = "0 in stock"
// The quantity box is ~5 characters wide; the full label clips there, and the
// cell beside it already says it in full.
const OUT_OF_STOCK_PLACEHOLDER = "None"
const UNLICENSED_BADGE_TITLE = "This state is not in the licensed states on your profile. "
  + "It never blocks a purchase; you will be asked to confirm before it is added to your cart."

export default class extends Controller {
  static values = {
    csrfToken: String,
    leadPriceUrl: String,
    addToCartUrl: String,
    userWebhookUrl: String,
    userGhlIntegration: Boolean,
    ghlEnabledDefault: Boolean,
    stateLookup: Object,
    // The licensed states of whoever the leads are for, for the unlicensed-state
    // advisory: the agent on an Agency Lead Order (#19074), else current_user
    // (AgedLeads::AvailabilityViewHelper#aged_availability_controller_data).
    licensedStates: Array,
    // Where the buyer edits that list — the confirm dialog links to it.
    profileUrl: String,
    googleSheetsEnabled: Boolean,
    googleSheetIntegrations: Array,
    ringyIntegrations: Array,
    minLeadsAged: { type: Number, default: 20 },
    // LeadOrder::INTEGRATION_NONE, rendered by the view so the sentinel is never hand-mirrored here.
    integrationOptOut: { type: String, default: "none" },
    agedCartTotal: { type: Number, default: 0 },
    // The disabled-reason copy for Lead Integration / Go to Cart, rendered by
    // the view from AgedCartRail so JS never re-words the server's sentence.
    cartEmptyReason: { type: String, default: "Add leads to your cart first" },
    cartMinimumReason: { type: String, default: "Your cart needs more aged leads" },
    // GOAT Store Buy switch (#14937): non-blank = every Add to Cart stays
    // disabled with this server-painted reason (ajax_add_to_cart refuses the
    // same way). Items already in the cart and Go to Cart are untouched.
    purchaseBlockedReason: { type: String, default: "" },
    // #18902: { lead_class: [qualities priced aged] } from
    // LeadPricingPackage.aged_store_qualities_by_type. A type absent from the
    // map leaves every quality offered (the server still refuses an unpriced pick).
    qualitiesByType: { type: Object, default: {} }
  }

  connect() {
    // One life per connect (#13900 / #13905). Every asynchronous continuation
    // captures `this.lifecycle` when its work starts and asks current() before
    // it touches anything: a response released after the dialog closed lands on
    // an instance that is no longer alive — or, since Stimulus keeps one
    // instance per element, on the same instance in a LATER life — and must
    // not hide the next dialog's loader, toast, refresh, draw on its canvas or
    // release its in-flight guard. The server's side of that request stands
    // (a closed dialog cancels no accepted purchase); only its UI effects drop.
    this.lifecycle = (this.lifecycle || 0) + 1
    this.alive = true
    this.availabilityRequest = 0
    this.heatmapDraw = 0
    this.heatmapInit = null // a previous life's atlas fetch is dropped by current(); it holds no slot here
    this.orderInFlight = false
    this.stateQuantities = {}
    this.selectedRingyIntegration = ""
    this.selectedWebhookUrl = ""
    this.selectedGhlEnabled = this.ghlEnabledDefaultValue || false
    this.selectedGhlTags = []
    this.selectedGoogleSheetIntegration = ""
    this.chartInstance = null

    this.cacheElements()
    this.selectedWebhookUrl = (this.webhookInput && this.webhookInput.value) || ""
    this.bindEvents()
    this.takeIntegrationBaseline()
    this.renderStateRows(this.buildEmptyStateRows(), null, "", "")
    this.initializeHeatmap(this.normalizedHeatmapData({}))
    this.ensureHeatmapLibraries()
    this.updateGuidedFocus()
  }

  // The page ships chart.js, topojson and chartjs-chart-geo from <head>; the
  // GOAT Store order dialog cannot carry <script> tags (MODAL_ADAPTER_CONTRACT v2
  // §4), so when the heat-map canvas is present and the libraries are not, load
  // them here — same jsdelivr URLs (allowed by the CSP host list), in dependency
  // order — then draw the pending data. If they fail to load, the heat map simply
  // stays absent: initializeHeatmap already guards on heatmapLibrariesReady().
  static HEATMAP_LIBRARIES = [
    "https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js",
    "https://cdn.jsdelivr.net/npm/topojson-client@3.1.0/dist/topojson-client.min.js",
    "https://cdn.jsdelivr.net/npm/chartjs-chart-geo@4.3.4/build/index.umd.min.js"
  ]

  // All three libraries are present only once the choropleth controller is
  // registered. `Chart` alone is chart.js, the FIRST script — and the scripts
  // outlive a closed dialog, so a reopen landing after chart.js had run but
  // before the geo plugin had would read the set as ready on `Chart` alone:
  // constructing the choropleth throws ("not a registered controller") and the
  // pending map is never drawn (#14097). TypedRegistry#get
  // answers undefined for an unknown id; Registry#getController would throw.
  heatmapLibrariesReady() {
    return typeof Chart !== "undefined"
      && typeof topojson !== "undefined"
      && Boolean(Chart.registry?.controllers?.get("choropleth"))
  }

  ensureHeatmapLibraries() {
    if (this.heatmapLibrariesReady() || !this.element.querySelector("#usaHeatmap")) return Promise.resolve()
    // A tag whose load FAILED is removed, so the next open re-inserts it: a
    // failed tag never fires `error` again, and waiting on it hung every later
    // open's chain with the map absent for good.
    const failed = (script, reject) => () => { script.remove(); reject(new Error(script.src)) }
    const load = (src) => new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`)
      if (existing) {
        if (existing.dataset.loaded === "true") return resolve()
        existing.addEventListener("load", () => resolve(), { once: true })
        existing.addEventListener("error", failed(existing, reject), { once: true })
        return
      }
      const script = document.createElement("script")
      script.src = src
      script.async = false
      script.addEventListener("load", () => { script.dataset.loaded = "true"; resolve() }, { once: true })
      script.addEventListener("error", failed(script, reject), { once: true })
      document.head.appendChild(script)
    })
    const lifecycle = this.lifecycle
    return this.constructor.HEATMAP_LIBRARIES
      .reduce((chain, src) => chain.then(() => load(src)), Promise.resolve())
      .then(() => {
        if (!this.current(lifecycle) || this.chartInstance) return
        this.initializeHeatmap(this.pendingHeatmapData || this.normalizedHeatmapData({}))
      })
      .catch(() => { /* heat map stays absent; the store keeps working */ })
  }

  // Guided cue: ring the next step that needs input (Lead Type, Lead Quality, then Bucket).
  setActiveStep(el) {
    [this.leadClassSelect, this.leadQualitySelect, this.ageSelect].forEach((s) => s?.classList.remove("gl-store-active"))
    if (el) el.classList.add("gl-store-active")
  }

  updateGuidedFocus() {
    if (!this.leadClassSelect?.value) {
      this.setActiveStep(this.leadClassSelect)
    } else if (!this.leadQualitySelect?.value) {
      this.setActiveStep(this.leadQualitySelect)
    } else if (!this.ageSelect?.value) {
      this.setActiveStep(this.ageSelect)
    } else {
      this.setActiveStep(null)
    }
  }

  disconnect() {
    this.alive = false
    this.destroyChart()
    this.removeLoading()
    this.unbindEvents()
  }

  // True while the life that started the work is the one still on screen. The
  // element check covers the gap between a DOM removal and Stimulus's
  // disconnect() (a mutation-observer callback), which is when a response can
  // still land.
  current(lifecycle) {
    return this.alive === true && lifecycle === this.lifecycle && this.element.isConnected
  }

  // Availability is also per request: a later request supersedes an earlier
  // one, so an earlier response never paints over newer rows or clears the
  // loader the newer request still owns.
  currentAvailability(lifecycle, request) {
    return this.current(lifecycle) && request === this.availabilityRequest
  }

  cacheElements() {
    this.ageSelect = this.element.querySelector("#aged_bucket_select")
    this.leadClassSelect = this.element.querySelector("#lead_class_select")
    // #18902: Premium and Standard are sold apart; the buyer picks one.
    this.leadQualitySelect = this.element.querySelector("#lead_quality_select")
    this.tableBody = this.element.querySelector("#state-availability-body")
    this.totalAvailabilityElement = this.element.querySelector("#total-availability")
    this.headerTotalAvailabilityElement = this.element.querySelector("#header-total-availability")
    // Mobile elements
    this.mobileCards = this.element.querySelector("#state-availability-mobile-cards")
    this.mobileFilterSheet = this.element.querySelector("#mobile-filter-sheet")
    this.mobileFilterOverlay = this.element.querySelector("#mobile-filter-overlay")
    this.mobileAgeSelect = this.element.querySelector("#mobile_aged_bucket_select")
    this.mobileLeadClassSelect = this.element.querySelector("#mobile_lead_class_select")
    this.mobileLeadQualitySelect = this.element.querySelector("#mobile_lead_quality_select")
    // Lead Integration Options is an in-scope collapsible section (#13900; it was
    // the nested #ringyModal). Scope every lookup to this controller's element
    // first so a store-dialog instance never grabs a page copy of the same ids;
    // document stays the fallback for hosts that mount the fields outside the root.
    const find = (id) => this.element.querySelector(`#${id}`) || document.getElementById(id)
    this.integrationPanel = find("agedIntegrationPanel")
    this.ringySelect = find("ringy_integration_select")
    this.webhookInput = find("webhook_url_input")
    this.ghlCheckbox = find("ghl_enabled_input")
    this.selectedGhlEnabled = this.ghlCheckbox ? this.ghlCheckbox.checked : this.selectedGhlEnabled
    this.googleSheetSelect = find("google_sheet_integration_select")
    this.saveIntegrationButton = find("saveIntegration")
    this.copyWebhookButton = find("copyWebhookFromProfile")
    this.ghlTagsSelect = find("aged_leads_ghl_tags_select")
    this.cartActionButtons = this.element.querySelectorAll('[data-aged-lead-availability-target="cartActionBtn"]')
    this.minLeadsBadge = this.element.querySelector('[data-aged-lead-availability-target="minLeadsBadge"]')
    this.cartRailBody = this.element.querySelector("#cart-rail-body")
    this.cartRailTotal = this.element.querySelector('[data-aged-lead-availability-target="cartRailTotal"]')
    this.cartRailCount = this.element.querySelector('[data-aged-lead-availability-target="cartRailCount"]')
    this.integrationNotice = this.element.querySelector('[data-aged-lead-availability-target="integrationNotice"]')
    this.cartCtaHint = this.element.querySelector('[data-aged-lead-availability-target="cartCtaHint"]')
    this.degradedNotice = this.element.querySelector('[data-aged-lead-availability-target="degradedNotice"]')
  }

  bindEvents() {
    this.leadClassChangeHandler = this.handleLeadClassChange.bind(this)
    this.leadQualityChangeHandler = this.handleLeadQualityChange.bind(this)
    this.ageChangeHandler = this.handleBucketChange.bind(this)
    this.quantityInputHandler = this.handleQuantityInput.bind(this)
    this.ringySelectChangeHandler = this.handleRingySelectChange.bind(this)
    this.ringyBtnHandler = this.handleRingyClick.bind(this)
    this.orderBtnHandler = this.handleOrderClick.bind(this)
    this.saveIntegrationHandler = this.handleSaveIntegration.bind(this)
    this.copyWebhookHandler = this.handleCopyWebhook.bind(this)
    this.ghlCheckboxHandler = this.handleGhlClick.bind(this)

    this.ghlTagsSelectChangeHandler = this.handleGhlTagsSelectChange.bind(this)
    this.modalHiddenHandler = this.handleModalHidden.bind(this)
    this.googleSheetSelectChangeHandler = this.handleGoogleSheetSelectChange.bind(this)

    this.cartRailClickHandler = this.handleCartRailClick.bind(this)
    this.cartRailBody?.addEventListener("click", this.cartRailClickHandler)

    this.ageSelect?.addEventListener("change", this.ageChangeHandler)
    this.leadClassSelect?.addEventListener("change", this.leadClassChangeHandler)
    this.leadQualitySelect?.addEventListener("change", this.leadQualityChangeHandler)
    this.ringySelect?.addEventListener("change", this.ringySelectChangeHandler)
    this.googleSheetSelect?.addEventListener("change", this.googleSheetSelectChangeHandler)
    this.webhookInput?.addEventListener("input", () => {
      this.selectedWebhookUrl = this.webhookInput.value || ""
      this.updateButtonStates()
    })
    this.ghlCheckbox?.addEventListener("click", (event) => {
      this.selectedGhlEnabled = event.target.checked
      this.ghlCheckboxHandler(event)
      this.updateButtonStates()
    })

    document.addEventListener("input", this.quantityInputHandler, true)
    document.addEventListener("click", this.orderBtnHandler, true)
    // The integration section opens/closes as a Bootstrap collapse; the same
    // handlers that ran on the former modal's show/hidden run here.
    this.integrationPanel?.addEventListener("show.bs.collapse", this.ringyBtnHandler)
    this.saveIntegrationButton?.addEventListener("click", this.saveIntegrationHandler)
    this.copyWebhookButton?.addEventListener("click", this.copyWebhookHandler)
    this.ghlTagsSelect?.addEventListener("change", this.ghlTagsSelectChangeHandler)
    this.integrationPanel?.addEventListener("hidden.bs.collapse", this.modalHiddenHandler)
    // BUTTON-9: every edit in the integration section re-gates its Save.
    this.integrationEditHandler = () => this.syncIntegrationSave()
    this.integrationEditSources().forEach((el) => {
      el.addEventListener("input", this.integrationEditHandler)
      el.addEventListener("change", this.integrationEditHandler)
    })
  }

  unbindEvents() {
    this.cartRailBody?.removeEventListener("click", this.cartRailClickHandler)
    this.ageSelect?.removeEventListener("change", this.ageChangeHandler)
    this.leadClassSelect?.removeEventListener("change", this.leadClassChangeHandler)
    this.leadQualitySelect?.removeEventListener("change", this.leadQualityChangeHandler)
    this.ringySelect?.removeEventListener("change", this.ringySelectChangeHandler)
    this.googleSheetSelect?.removeEventListener("change", this.googleSheetSelectChangeHandler)
    this.ghlCheckbox?.removeEventListener("click", this.ghlCheckboxHandler)
    document.removeEventListener("input", this.quantityInputHandler, true)
    document.removeEventListener("click", this.orderBtnHandler, true)
    this.integrationPanel?.removeEventListener("show.bs.collapse", this.ringyBtnHandler)
    this.saveIntegrationButton?.removeEventListener("click", this.saveIntegrationHandler)
    this.copyWebhookButton?.removeEventListener("click", this.copyWebhookHandler)
    this.ghlTagsSelect?.removeEventListener("change", this.ghlTagsSelectChangeHandler)
    this.integrationPanel?.removeEventListener("hidden.bs.collapse", this.modalHiddenHandler)
    this.integrationEditSources().forEach((el) => {
      el.removeEventListener("input", this.integrationEditHandler)
      el.removeEventListener("change", this.integrationEditHandler)
    })
  }

  // ── Integration section Save (BUTTON-9) ─────────────────────────────────
  // Save is disabled, with its reason, until a field differs from what the
  // section showed when it opened (or was last saved), and again if the edit
  // is reverted. The fields are cached individually because a host may mount
  // them outside the panel (see cacheElements).
  integrationEditSources() {
    const container = this.element.querySelector("#aged_leads_ghl_tags_container") ||
      document.getElementById("aged_leads_ghl_tags_container")
    return [this.ringySelect, this.googleSheetSelect, this.webhookInput, this.ghlCheckbox,
            this.ghlTagsSelect, container].filter(Boolean)
  }

  integrationFingerprint() {
    const fields = [this.ringySelect, this.googleSheetSelect, this.webhookInput, this.ghlCheckbox,
                    this.ghlTagsSelect].filter(Boolean)
    const container = this.element.querySelector("#aged_leads_ghl_tags_container") ||
      document.getElementById("aged_leads_ghl_tags_container")
    if (container) fields.push(...container.querySelectorAll("input"))
    return fields.map(fieldToken).join("|")
  }

  takeIntegrationBaseline() {
    this.integrationBaseline = this.integrationFingerprint()
    this.syncIntegrationSave()
  }

  syncIntegrationSave() {
    if (!this.saveIntegrationButton) return
    const dirty = this.integrationFingerprint() !== this.integrationBaseline
    syncGateReason(this.saveIntegrationButton, { ready: dirty, reason: SAVE_UNCHANGED_REASON })
  }

  handleBucketChange() {
    this.stateQuantities = {}
    this.renderStateRows(this.buildEmptyStateRows(), null, "", "")
    this.updateHeatmap({})
    if (this.hasAllFilters()) {
      this.fetchAvailability()
    }
    this.updateGuidedFocus()
  }

  setDependentFiltersEnabled(enabled) {
    this.setDisabled(this.ageSelect, !enabled)

    if (!enabled && this.ageSelect) this.ageSelect.value = ""
  }

  purchaseBlocked() {
    return this.purchaseBlockedReasonValue.trim() !== ""
  }

  setDisabled(element, disabled) {
    if (!element) return
    element.disabled = disabled
    if (disabled) {
      element.setAttribute("disabled", "disabled")
    } else {
      element.removeAttribute("disabled")
    }
  }

  hasAllFilters() {
    return Boolean(this.ageSelect?.value && this.prerequisiteFiltersChosen())
  }

  // Lead Type and Lead Quality both chosen: the Bucket step unlocks.
  prerequisiteFiltersChosen() {
    return Boolean(this.leadClassSelect?.value && this.leadQualitySelect?.value)
  }

  handleQuantityInput(event) {
    const input = event.target
    if (!input.classList?.contains("quantity-input")) return
    if (!this.element.contains(input)) return // another aged root's input (the page under a dialog)

    const max = parseInt(input.getAttribute("max"), 10) || 0
    const stateCode = input.dataset.state
    const sanitized = this.sanitizeQuantity(input.value, max)
    input.value = sanitized
    if (stateCode) {
      this.stateQuantities[stateCode] = sanitized
    }
    this.updateButtonStates()
  }

  handleRingyClick() {
    this.filterRingyOptions()
    if (this.ringySelect) this.ringySelect.value = this.selectedRingyIntegration || ""
    if (this.webhookInput) this.webhookInput.value = this.selectedWebhookUrl || ""
    if (this.ghlCheckbox) this.ghlCheckbox.checked = this.selectedGhlEnabled
    this.filterGoogleSheetOptions()
    if (this.googleSheetSelect) this.googleSheetSelect.value = this.selectedGoogleSheetIntegration || ""
    this.restoreSavedGhlTagPicks()
    this.takeIntegrationBaseline()
  }

  // BUTTON-9: opening the section shows the SAVED tags, like every other field
  // above, so the baseline it takes is the last saved state. Picks made and
  // then abandoned (Close without Save) are dropped instead of becoming the
  // new "unchanged" state.
  restoreSavedGhlTagPicks() {
    if (!this.ghlTagsSelect) return
    const saved = new Set(this.selectedGhlTags)
    Array.from(this.ghlTagsSelect.options).forEach((option) => {
      option.selected = saved.has(option.value)
    })
  }

  handleSaveIntegration(event) {
    event.preventDefault()
    if (this.ringySelect) this.selectedRingyIntegration = this.ringySelect.value || ""
    if (this.webhookInput) this.selectedWebhookUrl = this.webhookInput.value || ""
    if (this.ghlCheckbox) this.selectedGhlEnabled = this.ghlCheckbox.checked
    if (this.googleSheetSelect) this.selectedGoogleSheetIntegration = this.googleSheetSelect.value || ""

    this.selectedGhlTags = this.getSelectedGhlTags()

    const assignIntegrationData = (btn) => {
      btn.dataset.ringyIntegrationId = this.selectedRingyIntegration
      btn.dataset.webhookUrl = this.selectedWebhookUrl
      btn.dataset.ghlEnabled = this.selectedGhlEnabled
      btn.dataset.ghlTags = JSON.stringify(this.selectedGhlTags)
      btn.dataset.googleSheetIntegrationId = this.selectedGoogleSheetIntegration
    }

    this.element.querySelectorAll(".order-btn").forEach(assignIntegrationData)

    this.updateButtonStates()
    this.takeIntegrationBaseline()

    // Saving closes the inline section (it hid the former modal).
    if (this.integrationPanel) bootstrap.Collapse.getOrCreateInstance(this.integrationPanel, { toggle: false }).hide()
  }

  handleCopyWebhook(event) {
    event.preventDefault()
    if (!this.webhookInput) return
    this.webhookInput.value = this.userWebhookUrlValue || ""
    // A programmatic value write fires no `input`, so the copied URL would
    // reach neither `selectedWebhookUrl` nor the button states (#14273 item
    // 10). Say it the way a typed edit does.
    this.webhookInput.dispatchEvent(new Event("input", { bubbles: true }))
  }

  handleRingySelectChange(event) {
    this.selectedRingyIntegration = event.target.value || ""
    this.updateButtonStates()
  }

  handleGoogleSheetSelectChange(event) {
    this.selectedGoogleSheetIntegration = event.target.value || ""
    this.updateButtonStates()
  }

  handleLeadClassChange() {
    this.filterQualityOptions()
    this.filterRingyOptions()
    this.filterGoogleSheetOptions()
    this.handlePrerequisiteFilterChange()
    if (this.leadClassSelect?.value && !this.leadQualitySelect?.value) this.leadQualitySelect?.focus()
  }

  handleLeadQualityChange() {
    this.handlePrerequisiteFilterChange()
  }

  // Lead Type or Lead Quality changed: the rows on screen no longer match, so
  // clear them, unlock Bucket only once both are chosen, and refetch when ready.
  handlePrerequisiteFilterChange() {
    const ready = this.prerequisiteFiltersChosen()
    this.setDependentFiltersEnabled(ready)
    this.stateQuantities = {}
    this.renderStateRows(this.buildEmptyStateRows(), null, "", "")
    this.updateHeatmap({})
    if (this.hasAllFilters()) {
      this.fetchAvailability()
    }
    this.updateGuidedFocus()
    if (ready && !this.ageSelect?.value) this.ageSelect?.focus()
  }

  // #18902: offer only the qualities priced aged for the picked lead type, so
  // Widow (no Standard aged prices) shows Premium alone. A pick the new type
  // cannot sell is cleared; with no type picked every quality shows.
  filterQualityOptions() {
    const leadClass = this.leadClassSelect?.value
    const priced = leadClass ? this.qualitiesByTypeValue[leadClass] : null
    ;[this.leadQualitySelect, this.mobileLeadQualitySelect].forEach((select) => {
      if (!select) return
      Array.from(select.options).forEach((option) => {
        if (!option.value) return
        const offered = !priced || priced.includes(option.value)
        option.hidden = !offered
        option.disabled = !offered
      })
      if (select.selectedOptions[0]?.disabled) select.value = ""
    })
  }

  filterGoogleSheetOptions() {
    if (!this.googleSheetSelect) return

    const selectedLeadClass = this.leadClassSelect?.value || ""
    const integrations = this.googleSheetIntegrationsValue || []

    // Rebuild keeping the fixed choices (blank = default, "none" = opt out).
    this.rebuildFixedOptions(this.googleSheetSelect)

    const filteredIntegrations = integrations.filter(
      (integration) => integration.lead_type === selectedLeadClass
    )

    filteredIntegrations.forEach((integration) => {
      const option = document.createElement("option")
      option.value = integration.id
      option.textContent = integration.name
      this.googleSheetSelect.appendChild(option)
    })

    // Reset selection if current selection is not valid for the new lead type
    // Or restore the value if it IS valid (since rebuilding options clears the select value)
    if (this.selectedGoogleSheetIntegration) {
      const isValidSelection = this.isOptOut(this.selectedGoogleSheetIntegration) || filteredIntegrations.some(
        (i) => String(i.id) === String(this.selectedGoogleSheetIntegration)
      )
      if (isValidSelection) {
        this.googleSheetSelect.value = this.selectedGoogleSheetIntegration
      } else {
        this.selectedGoogleSheetIntegration = ""
        this.googleSheetSelect.value = ""
      }
    }

    this.updateGoogleSheetNoIntegrationsMessage(filteredIntegrations.length === 0 && selectedLeadClass)
  }

  filterRingyOptions() {
    if (!this.ringySelect) return

    const selectedLeadClass = this.leadClassSelect?.value || ""
    const integrations = this.ringyIntegrationsValue || []

    // Rebuild keeping the fixed choices (blank = default, "none" = opt out).
    this.rebuildFixedOptions(this.ringySelect)

    // Filter: include integrations where lead_type matches OR lead_type is null/empty
    const filteredIntegrations = integrations.filter(
      (integration) => integration.lead_type === selectedLeadClass || !integration.lead_type
    )

    filteredIntegrations.forEach((integration) => {
      const option = document.createElement("option")
      option.value = integration.id
      option.textContent = integration.name
      this.ringySelect.appendChild(option)
    })

    // Reset selection if current selection is not valid for the new lead type
    if (this.selectedRingyIntegration) {
      const isValidSelection = this.isOptOut(this.selectedRingyIntegration) || filteredIntegrations.some(
        (i) => String(i.id) === String(this.selectedRingyIntegration)
      )
      if (isValidSelection) {
        this.ringySelect.value = this.selectedRingyIntegration
      } else {
        this.selectedRingyIntegration = ""
        this.ringySelect.value = ""
      }
    }

    this.updateRingyNoIntegrationsMessage(filteredIntegrations.length === 0 && selectedLeadClass)
  }

  // The integration selects are three-state (#12785): "" = the agent's default
  // for the lead type — resolved by the SERVER at create
  // (LeadOrder#set_default_integrations), so this page never auto-selects
  // a default client-side and the two resolvers cannot disagree —
  // integrationOptOutValue (LeadOrder::INTEGRATION_NONE) = a deliberate
  // per-order opt-out, or an integration id. Rebuilding the option list on a
  // lead-class change keeps the opt-out option and its selection: an opt-out
  // reset to blank would silently turn into the default, the exact failure the
  // sentinel exists to prevent.
  isOptOut(value) {
    return String(value) === this.integrationOptOutValue
  }

  // Keeps the server-rendered blank + none options (their labels included); a
  // missing blank is recreated with the same "use my default" copy the views
  // render, never the retired "Select …" wording.
  rebuildFixedOptions(select) {
    const fixed = Array.from(select.options).filter(
      (option) => option.value === "" || this.isOptOut(option.value)
    )
    select.innerHTML = ""
    if (!fixed.some((option) => option.value === "")) {
      const blankOption = document.createElement("option")
      blankOption.value = ""
      blankOption.textContent = INTEGRATION_DEFAULT_LABEL
      select.appendChild(blankOption)
    }
    fixed.forEach((option) => select.appendChild(option))
  }

  updateRingyNoIntegrationsMessage(showMessage) {
    const section = this.ringySelect?.closest(".gl-modal-section")
    if (!section) return

    let noMsg = section.querySelector(".no-ringy-integrations-msg")

    if (showMessage) {
      if (!noMsg) {
        noMsg = document.createElement("p")
        noMsg.className = "gl-modal-hint no-ringy-integrations-msg"
        noMsg.textContent = "No Ringy integrations found for this lead type."
        this.ringySelect.insertAdjacentElement("afterend", noMsg)
      }
      setHidden(this.ringySelect, true)
      setHidden(noMsg, false)
    } else {
      if (noMsg) setHidden(noMsg, true)
      setHidden(this.ringySelect, false)
    }
  }

  updateGoogleSheetNoIntegrationsMessage(showMessage) {
    const container = this.googleSheetSelect?.closest(".col-12")
    if (!container) return

    let noIntegrationsMsg = container.querySelector(".no-google-sheet-integrations-msg")

    if (showMessage) {
      if (!noIntegrationsMsg) {
        noIntegrationsMsg = document.createElement("p")
        noIntegrationsMsg.className = "text-muted no-google-sheet-integrations-msg"
        noIntegrationsMsg.textContent = "No Google Sheet integrations found for this lead type."
        this.googleSheetSelect.insertAdjacentElement("afterend", noIntegrationsMsg)
      }
      setHidden(this.googleSheetSelect, true)
      setHidden(noIntegrationsMsg, false)
    } else {
      if (noIntegrationsMsg) setHidden(noIntegrationsMsg, true)
      setHidden(this.googleSheetSelect, false)
    }
  }

  handleGhlClick(event) {
    const hasGhlIntegration = this.userGhlIntegrationValue === true || this.userGhlIntegrationValue === "true"
    if (event.target.disabled && !hasGhlIntegration) {
      event.preventDefault()
      window.location.href = event.target.dataset.profilePath
    }
  }

  handleModalHidden() {
    this.updateButtonStates()
  }

  // Both the `input` and `click` listeners are delegated from `document`, so a
  // second aged root on the same page (the standalone page under the GOAT Store
  // dialog) would see the other root's events too — two controllers, two
  // POSTs for one press. Each controller answers only inside its own element.
  handleOrderClick(event) {
    const btn = event.target.closest(".order-btn")
    if (!btn || btn.disabled || !this.element.contains(btn)) return
    event.preventDefault()

    // preventDefault() has to be synchronous, so the confirm can only run after
    // it — the POST itself is deferred into performOrder().
    const offenders = unlicensedStates([btn.dataset.state], this.licensedStatesList)
    if (offenders.length === 0) {
      this.performOrder(btn)
      return
    }

    const lifecycle = this.lifecycle
    confirmUnlicensedStates(offenders, {
      licensed: this.licensedStatesList,
      actionLabel: "add it to the cart",
      profileUrl: this.hasProfileUrlValue ? this.profileUrlValue : null,
    }).then((accepted) => {
      // Re-check isConnected: an availability refresh can replace the row (and
      // its button) while the modal is open, and a detached button carries the
      // dataset of a state/quantity that is no longer on screen. And the life:
      // a confirm answered after the dialog closed is not a purchase.
      if (accepted && btn.isConnected && this.current(lifecycle)) this.performOrder(btn)
    })
  }

  // One add-to-cart request at a time (#14207). The loader paints below the
  // dialog, so it never kept a second press off the button, and the server's
  // row-locked claim partitions inventory between requests rather than deduping
  // them: four presses under a slow network were four POSTs, and four orders
  // once the bucket held enough leads. The flag is controller-wide because the
  // desktop rows and the mobile cards are two views of one purchase. It holds
  // through the availability refresh that follows a success (the rows come back
  // with the spent stock) and is released on any failure so a deliberate retry
  // goes through.
  performOrder(btn) {
    if (this.orderInFlight) return
    this.orderInFlight = true
    const lifecycle = this.lifecycle
    this.updateButtonStates()
    // BUTTON-8: the pressed Add to Cart keeps its label and shows the spinner.
    this.busyOrderButton = btn
    setButtonLoading(btn, true)
    this.showLoading()
    const leadClass = btn.dataset.leadClass
    const leadQuality = btn.dataset.leadQuality
    const state = btn.dataset.state
    const quantity = btn.dataset.quantity
    const agedBucket = btn.dataset.agedBucket
    // The SAVED integration state only (#14273 item 10). Reading the panel's
    // live selects/inputs first meant an edit the buyer never saved — or
    // closed the panel to discard — still went out with the order, while the
    // reopened panel showed the saved values. `selected*` is what Save and the
    // panel's own change handlers wrote; the button dataset mirrors it.
    const ringyIntegrationId = this.selectedRingyIntegration || ""
    const webhookUrl = this.selectedWebhookUrl || ""
    const ghlEnabledFlag = this.selectedGhlEnabled ? "1" : "0"
    const googleSheetIntegrationId = this.selectedGoogleSheetIntegration || ""

    const selectedGhlTags = this.getSelectedGhlTags()

    const params = new URLSearchParams({
      authenticity_token: this.csrfTokenValue,
      "aged_lead_order[lead_class]": leadClass || "",
      "aged_lead_order[lead_quality]": leadQuality || "",
      "aged_lead_order[states][]": state || "",
      "aged_lead_order[total_lead_order]": quantity || 0,
      "aged_lead_order[aged_bucket]": agedBucket || "",
      "aged_lead_order[aged]": true,
      "aged_lead_order[ringy_integration_id]": ringyIntegrationId || "",
      "aged_lead_order[webhook_url]": webhookUrl || "",
      "aged_lead_order[ghl_enabled]": ghlEnabledFlag,
      "aged_lead_order[google_sheet_integration_id]": googleSheetIntegrationId || ""
    })

    selectedGhlTags.forEach(tag => {
      params.append("aged_lead_order[ghl_tags][]", tag)
    })

    post(this.addToCartUrlValue, { headers: this.postHeaders(), body: params, responseKind: "json" })
      .then((response) => this.ensureJson(response))
      .then((data) => {
        // The dialog may have closed while the request ran: the order is carted
        // server-side and the cart shows it; nothing here has a screen any more.
        if (!this.current(lifecycle)) return undefined
        this.hideLoading()
        this.showToast(`Aged ${data.lead_class} Order Added to Cart: $${data.amount}`, "success")
        this.agedCartTotalValue = parseInt(data.aged_cart_total, 10) || this.agedCartTotalValue
        this.updateCartActionButtons()
        if (data.rail) this.renderCartRail(data.rail)
        // The pressed button is disabled and then re-rendered, which drops focus
        // to <body>; inside the GOAT Store dialog that also puts Escape out of
        // Bootstrap's reach (its keydown listener sits on the modal element).
        return this.fetchAvailability().then(() => this.focusQuantityInputFor(state))
      })
      .catch((error) => {
        if (!this.current(lifecycle)) return
        console.error("Order error", error)
        this.hideLoading()
        const message = error?.message || "Error adding order to cart. Please try again."
        this.showToast(message, "danger")
      })
      .finally(() => { if (this.current(lifecycle)) this.releaseOrder() })
  }

  releaseOrder() {
    this.orderInFlight = false
    // A success re-renders the rows, so the pressed button may already be gone;
    // on a failure it is still here and drops its spinner before the gates run.
    setButtonLoading(this.busyOrderButton, false)
    this.busyOrderButton = null
    this.updateButtonStates()
  }

  // After a purchase, put the keyboard back on the state that was just bought:
  // its visible quantity input (desktop row or mobile card). Never steals focus
  // from a control the buyer has meanwhile moved to.
  focusQuantityInputFor(stateCode) {
    if (!this.element.isConnected) return
    const active = document.activeElement
    if (active && active !== document.body && this.element.contains(active)) return
    const twins = [...this.element.querySelectorAll(".quantity-input")].filter((input) => input.dataset.state === stateCode)
    const target = twins.find((input) => input.offsetParent !== null) || twins[0]
    target?.focus()
  }

  // Resolves once the rows are re-rendered (or the failure is shown); a caller
  // that must stay guarded until the counts are current awaits it.
  fetchAvailability() {
    // Counted before the empty-filter branch: clearing a filter while an older
    // response is pending makes that response obsolete too, and the empty
    // state settles the loader the obsolete request had shown.
    const lifecycle = this.lifecycle
    const request = ++this.availabilityRequest
    if (!this.hasAllFilters()) {
      this.renderStateRows(this.buildEmptyStateRows(), null, "", "")
      this.updateHeatmap({})
      this.hideLoading()
      return Promise.resolve()
    }

    this.showLoading()
    this.cacheQuantities()
    return post(this.leadPriceUrlValue, {
      headers: this.postHeaders(),
      responseKind: "json",
      body: new URLSearchParams({
        authenticity_token: this.csrfTokenValue,
        aged_bucket: this.ageSelect.value,
        lead_class: this.leadClassSelect.value,
        lead_quality: this.leadQualitySelect.value
      })
    })
      .then((response) => this.ensureJson(response))
      .then((data) => {
        if (!this.currentAvailability(lifecycle, request)) return
        const price = data.price
        const ageLabel = data.age_label || ""
        const agedBucket = data.aged_bucket || ""
        const states = data.states || this.buildEmptyStateRows()

        this.renderStateRows(states, price, ageLabel, agedBucket)
        this.updateHeatmap(data.state_lead_counts || {})
        this.renderDegradedNotice(data.notice)
        this.showAdminPricingWarnings(data.admin_pricing_warnings)
        this.hideLoading()
      })
      .catch((error) => {
        if (!this.currentAvailability(lifecycle, request)) return
        console.error("Availability error", error)
        this.hideLoading()
        // Surface the failure instead of silently leaving the spinner gone with no
        // counts — e.g. an eligibility-gate 422 (ensureJson puts the gate's message
        // on error.message) now tells the user what to do rather than failing mute.
        const message = error?.message || "Couldn't load availability. Please try again."
        this.showToast(message, "danger")
      })
  }

  // #13646: a degraded server count (Redis blip, eligibility-table swap) is
  // still a 200 with counts; the payload's `notice` says so and this shows it
  // in the page-level status alert. No notice → hidden, so it clears itself on
  // the next healthy poll.
  renderDegradedNotice(notice) {
    const el = this.degradedNotice
    if (!el) return
    const content = el.querySelector(".gl-alert-content") || el
    if (notice) {
      content.textContent = notice
      el.hidden = false
    } else {
      content.textContent = ""
      el.hidden = true
    }
  }

  // The desktop row and the mobile card of a state are twins that share a
  // data-state; only one is visible at any width and handleQuantityInput keeps
  // stateQuantities current from whichever the buyer types into. A blank twin
  // therefore carries no information: reading it here overwrote a typed desktop
  // value with the hidden card's "" on every refresh (an add, a remove, a bucket
  // change), so the buyer's quantities vanished after each purchase.
  cacheQuantities() {
    this.element.querySelectorAll(".quantity-input").forEach((input) => {
      const stateCode = input.dataset.state
      if (!stateCode || input.value === "") return
      this.stateQuantities[stateCode] = input.value
    })
  }

  buildEmptyStateRows() {
    return Object.entries(this.stateLookupValue || {}).map(([code, name]) => ({ code, name, availability: 0 }))
  }

  sanitizeQuantity(value, max) {
    const cleaned = String(value || "").replace(/[^0-9]/g, "")
    if (cleaned === "") return ""
    const intVal = parseInt(cleaned, 10)
    if (Number.isNaN(intVal)) return ""
    if (max && intVal > max) return max
    if (intVal < 0) return ""
    return intVal
  }

  stateFlagUrl(stateCode) {
    const code = stateCode.toLowerCase()
    if (code === "dc") return "https://flagcdn.com/w40/us.png"
    return `https://flagcdn.com/w40/us-${code}.png`
  }

  // --- Unlicensed-state advisory ---
  //
  // This store adds ONE state per cart click, so the inline half of the
  // advisory is a per-row pill rather than a summary line: the buyer is
  // choosing among 50 rows and needs the flag where the row is.

  get licensedStatesList() {
    return this.hasLicensedStatesValue ? this.licensedStatesValue : []
  }

  isUnlicensedState(stateCode) {
    return unlicensedStates([stateCode], this.licensedStatesList).length > 0
  }

  // The count beside the bar. A zero is not a number to compare, it is the
  // reason the row's controls are disabled, so it says so in words.
  availCountMarkup(max) {
    if (max < 1) {
      return `<span class="gl-avail-count gl-avail-count--empty">${escapeHtml(OUT_OF_STOCK_LABEL)}</span>`
    }
    return `<span class="gl-avail-count">${escapeHtml(max)}</span>`
  }

  // Muted pill next to the state name. Empty string when the state IS licensed,
  // or when no licenses are on file at all (nothing to compare against — see
  // unlicensedStates()).
  //
  // Deliberately the grey `--inactive` family, not amber: this is advisory and
  // never disables a control, but an amber warning chip sitting beside a
  // quantity box that is greyed out for a DIFFERENT reason (0 in stock) reads
  // as the cause. Agents took it for a license block. The chip stays; it just
  // stops shouting, and the disabled box now names its own reason inline.
  unlicensedStateBadgeMarkup(stateCode) {
    if (!this.isUnlicensedState(stateCode)) return ""

    return `<span class="${statusBadgeClass("inactive")} ms-2" `
      + `title="${escapeHtml(UNLICENSED_BADGE_TITLE)}">Not on your licensed list</span>`
  }

  updateCartActionButtons() {
    const meetsMinimum = this.agedCartTotalValue >= this.minLeadsAgedValue
    this.cartActionButtons.forEach((btn) => {
      if (meetsMinimum) {
        btn.classList.remove("disabled")
        btn.removeAttribute("aria-disabled")
        btn.removeAttribute("tabindex")
        btn.removeAttribute("disabled")
        btn.removeAttribute("data-gl-tooltip")
      } else {
        btn.classList.add("disabled")
        btn.setAttribute("aria-disabled", "true")
        btn.setAttribute("tabindex", "-1")
        btn.setAttribute("disabled", "disabled")
        btn.setAttribute("data-gl-tooltip", this.cartActionDisabledReason())
      }
    })
    this.updateCartCtaHint(meetsMinimum)
  }

  // #13599: the VISIBLE twin of the CTA's disabled reason under the rail's Go
  // to Cart — a tooltip on a tabindex=-1 link is unreachable on touch. Same
  // sentence as the tooltip, hidden (and emptied, so aria-describedby reads
  // nothing) once the minimum is met. Mirrors the first paint in _cart_rail.
  updateCartCtaHint(meetsMinimum) {
    if (!this.cartCtaHint) return
    this.cartCtaHint.textContent = meetsMinimum ? "" : this.cartActionDisabledReason()
    this.cartCtaHint.classList.toggle("d-none", meetsMinimum)
  }

  // Why the cart CTAs are disabled right now: nothing in the cart, or a cart
  // still short of the aged minimum.
  cartActionDisabledReason() {
    return this.agedCartTotalValue > 0 ? this.cartMinimumReasonValue : this.cartEmptyReasonValue
  }

  // ---- A11Y-1 disabled reasons for the per-state controls -------------------------
  // A reason is either { describedBy: id } (the guided-steps hint, while the
  // filters are unset) or { tooltip: text }; null means the control is enabled
  // and must carry neither attribute.

  quantityDisabledReason({ filtersReady, availability }) {
    if (!filtersReady) return { describedBy: STEPS_HINT_ID }
    if (availability < 1) return { tooltip: NO_LEADS_REASON }
    return null
  }

  orderButtonDisabledReason({ filtersReady, availability, quantity, inFlight = false }) {
    if (this.purchaseBlocked()) return { tooltip: this.purchaseBlockedReasonValue }
    if (inFlight) return { tooltip: IN_FLIGHT_REASON }
    if (!filtersReady) return { describedBy: STEPS_HINT_ID }
    if (availability < 1) return { tooltip: NO_LEADS_REASON }
    if (quantity < 1) return { tooltip: QUANTITY_REASON }
    return null
  }

  // The attribute string for freshly rendered markup (values escaped).
  disabledReasonAttrs(reason) {
    if (reason?.describedBy) return `aria-describedby="${escapeHtml(reason.describedBy)}"`
    if (reason?.tooltip) return `data-gl-tooltip="${escapeHtml(reason.tooltip)}"`
    return ""
  }

  // The same reason applied to a live element: both attributes follow the
  // reason exactly, so an enabled control never keeps a stale explanation.
  applyDisabledReason(el, reason) {
    if (!el) return
    if (reason?.describedBy) {
      el.setAttribute("aria-describedby", reason.describedBy)
    } else {
      el.removeAttribute("aria-describedby")
    }
    if (reason?.tooltip) {
      el.setAttribute("data-gl-tooltip", reason.tooltip)
    } else {
      el.removeAttribute("data-gl-tooltip")
    }
  }

  // Builds the availability bar markup, color-tiered by how full the bar is
  // relative to the busiest state in the current result (high/med/low/empty).
  availBarMarkup(max, maxAvail) {
    const pct = maxAvail > 0 ? Math.round((max / maxAvail) * 100) : 0
    const tier = pct >= 66 ? "high" : pct >= 33 ? "med" : pct > 0 ? "low" : "empty"
    return `<span class="gl-avail-bar"><span class="gl-avail-bar-fill gl-avail-bar-fill--${tier}" style="width: ${pct}%"></span></span>`
  }

  renderStateRows(states, price, ageLabel, agedBucket) {
    if (!this.tableBody) return
    this.tableBody.innerHTML = ""

    const maxAvail = states.reduce((m, s) => Math.max(m, parseInt(s.availability, 10) || 0), 0)
    const availBar = (max) => this.availBarMarkup(max, maxAvail)

    const filtersReady = this.hasAllFilters()
    states.forEach((state) => {
      const max = parseInt(state.availability, 10) || 0
      const previousValue = this.sanitizeQuantity(this.stateQuantities[state.code], max)
      this.stateQuantities[state.code] = previousValue
      const disableCartBtn = this.purchaseBlocked() || !filtersReady || max < 1 || !previousValue
      const quantityReason = max < 1 ? this.quantityDisabledReason({ filtersReady, availability: max }) : null
      const orderReason = disableCartBtn
        ? this.orderButtonDisabledReason({ filtersReady, availability: max, quantity: parseInt(previousValue, 10) || 0 })
        : null

      const row = document.createElement("tr")
      row.classList.add("state-row")
      row.id = `state-row-${state.code}`
      row.dataset.state = state.code
      row.dataset.availability = max
      row.dataset.agedBucket = agedBucket || ""
      row.dataset.ageLabel = ageLabel || ""

      row.innerHTML = `
        <td class="fw-semibold">
          <img src="${escapeHtml(this.stateFlagUrl(state.code))}" alt="" width="20" height="20" class="me-2 align-text-bottom rounded-circle" loading="lazy">
          ${escapeHtml(state.name)}${this.unlicensedStateBadgeMarkup(state.code)}
        </td>
        <td class="text-center price">${valueOrEmptyHtml(price)}</td>
        <td class="text-center availability">
          <div class="gl-avail-cell">
            ${availBar(max)}
            ${this.availCountMarkup(max)}
          </div>
        </td>
        <td class="text-center">
          <input type="number"
            min="0"
            max="${escapeHtml(max)}"
            data-state="${escapeHtml(state.code)}"
            class="gl-form-control form-control-sm quantity-input"
            value="${max < 1 ? "" : escapeHtml(previousValue)}"
            ${max < 1 ? `disabled placeholder="${escapeHtml(OUT_OF_STOCK_PLACEHOLDER)}"` : ""}
            ${this.disabledReasonAttrs(quantityReason)}
            aria-label="Quantity for ${escapeHtml(state.name)}">
        </td>
        <td class="text-center">
          <button type="button"
            class="gl-btn gl-btn-outline gl-btn-sm order-btn"
            ${disableCartBtn ? "disabled" : ""}
            ${this.disabledReasonAttrs(orderReason)}
            data-state="${escapeHtml(state.code)}">
            <i class="ph-fill ph-shopping-cart me-1"></i> Add to Cart
          </button>
        </td>
      `

      this.tableBody.appendChild(row)
    })

    this.updateButtonStates()
    this.updateTotalAvailability(states)
    this.renderMobileCards(states, price, ageLabel, agedBucket)
  }

  updateTotalAvailability(states) {
    if (!this.totalAvailabilityElement) return

    const total = states.reduce((sum, state) => {
      return sum + (parseInt(state.availability, 10) || 0)
    }, 0)

    if (total === 0 && !this.hasAllFilters()) {
      setValueOrEmpty(this.totalAvailabilityElement, null)
      if (this.headerTotalAvailabilityElement) {
        this.headerTotalAvailabilityElement.textContent = ""
        this.headerTotalAvailabilityElement.classList.add("d-none")
      }
    } else {
      this.totalAvailabilityElement.textContent = total.toLocaleString()
      if (this.headerTotalAvailabilityElement) {
        this.headerTotalAvailabilityElement.textContent = total.toLocaleString()
        this.headerTotalAvailabilityElement.classList.remove("d-none")
      }
    }
  }

  updateButtonStates() {
    const filtersReady = this.hasAllFilters()
    const inFlight = this.orderInFlight === true
    this.element.querySelectorAll(".state-row").forEach((row) => {
      const availability = parseInt(row.dataset.availability, 10) || 0
      const stateCode = row.dataset.state
      const agedBucket = row.dataset.agedBucket
      const ageLabel = row.dataset.ageLabel
      const quantityInput = row.querySelector(".quantity-input")
      const quantity = parseInt(quantityInput?.value, 10) || 0
      const disableCartBtn = this.purchaseBlocked() || inFlight || !filtersReady || availability < 1 || quantity < 1
      const orderReason = disableCartBtn
        ? this.orderButtonDisabledReason({ filtersReady, availability, quantity, inFlight })
        : null

      row.querySelectorAll(".order-btn").forEach((btn) => {
        btn.disabled = disableCartBtn
        this.applyDisabledReason(btn, orderReason)
        btn.dataset.quantity = quantity
        btn.dataset.leadClass = this.leadClassSelect?.value || ""
        btn.dataset.leadQuality = this.leadQualitySelect?.value || ""
        btn.dataset.state = stateCode
        btn.dataset.agedBucket = agedBucket || ""
        btn.dataset.ageLabel = ageLabel || ""
        btn.dataset.ringyIntegrationId = (this.ringySelect && this.ringySelect.value)
          || this.selectedRingyIntegration
          || btn.dataset.ringyIntegrationId
          || ""
        btn.dataset.webhookUrl = (this.webhookInput && this.webhookInput.value)
          || this.selectedWebhookUrl
          || btn.dataset.webhookUrl
          || ""
        btn.dataset.ghlEnabled = this.ghlCheckbox ? this.ghlCheckbox.checked : this.selectedGhlEnabled
        btn.dataset.googleSheetIntegrationId = (this.googleSheetSelect && this.googleSheetSelect.value)
          || this.selectedGoogleSheetIntegration
          || btn.dataset.googleSheetIntegrationId
          || ""
      })

    })

    // Also update mobile card button states
    this.updateMobileCardButtonStates(filtersReady, inFlight)
  }

  updateMobileCardButtonStates(filtersReady, inFlight = false) {
    if (!this.mobileCards) return

    this.mobileCards.querySelectorAll(".gl-availability-card").forEach((card) => {
      const availability = parseInt(card.dataset.availability, 10) || 0
      const stateCode = card.dataset.state
      const agedBucket = card.dataset.agedBucket
      const ageLabel = card.dataset.ageLabel
      const quantityInput = card.querySelector(".quantity-input")
      const quantity = parseInt(quantityInput?.value, 10) || 0
      const disableBtn = this.purchaseBlocked() || inFlight || !filtersReady || availability < 1 || quantity < 1
      const orderReason = disableBtn
        ? this.orderButtonDisabledReason({ filtersReady, availability, quantity, inFlight })
        : null

      card.querySelectorAll(".order-btn").forEach((btn) => {
        btn.disabled = disableBtn
        this.applyDisabledReason(btn, orderReason)
        btn.dataset.quantity = quantity
        btn.dataset.leadClass = this.leadClassSelect?.value || ""
        btn.dataset.leadQuality = this.leadQualitySelect?.value || ""
        btn.dataset.state = stateCode
        btn.dataset.agedBucket = agedBucket || ""
        btn.dataset.ageLabel = ageLabel || ""
        btn.dataset.ringyIntegrationId = this.selectedRingyIntegration || ""
        btn.dataset.webhookUrl = this.selectedWebhookUrl || ""
        btn.dataset.ghlEnabled = this.selectedGhlEnabled
        btn.dataset.googleSheetIntegrationId = this.selectedGoogleSheetIntegration || ""
      })

    })
  }

  normalizedHeatmapData(rawCounts) {
    const normalized = {}
    Object.entries(this.stateLookupValue || {}).forEach(([abbr, name]) => {
      normalized[name] = rawCounts && rawCounts[abbr] ? rawCounts[abbr] : 0
    })
    return normalized
  }

  // One atlas fetch at a time: connect(), a filter change and the availability
  // response each asked for a map before the first atlas had landed, so three
  // inits raced for one canvas — Chart.js threw "Canvas is already in use" for
  // the losers, and when the EMPTY init won the map showed zeros beside a table
  // full of counts. The latest data always wins: it is what the one init draws.
  initializeHeatmap(heatmapData) {
    this.pendingHeatmapData = heatmapData
    if (this.heatmapInit) return this.heatmapInit
    this.destroyChart()
    // This instance's canvas only: the id is shared between dialog lives, and a
    // stale draw on the next dialog's canvas is "Canvas is already in use".
    const canvas = this.element.querySelector("#usaHeatmap")
    if (!canvas) return
    if (!this.heatmapLibrariesReady()) return // drawn once ensureHeatmapLibraries resolves

    const lifecycle = this.lifecycle
    const draw = ++this.heatmapDraw
    const init = fetch("https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json")
      .then((response) => response.json())
      .then((us) => {
        // A stale draw builds nothing: the dialog may have closed while the
        // atlas was loading (disconnect() destroyed the chart, and a chart
        // built now would leak with its canvas — or land on the NEXT dialog's
        // canvas), a later draw may have superseded this one, or the canvas
        // itself may have been replaced under a still-live element.
        if (!this.current(lifecycle) || draw !== this.heatmapDraw || !canvas.isConnected) return
        const data = this.pendingHeatmapData || heatmapData
        const states = topojson.feature(us, us.objects.states).features
        this.chartInstance = new Chart(canvas.getContext("2d"), {
          type: "choropleth",
          data: {
            labels: states.map((d) => d.properties.name),
            datasets: [{
              label: "Leads by State",
              data: states.map((d) => ({
                value: data[d.properties.name] || 0,
                feature: d
              }))
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            showOutline: false,
            showGraticule: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `${ctx.raw.feature.properties.name}: ${ctx.raw.value} leads`
                }
              }
            },
            scales: {
              color: {
                axis: "y",
                quantize: 5,
                legend: { display: false },
                interpolate: "interpolateReds"
              }
            }
          }
        })
      })
      .catch((error) => console.error("Map load error", error))
      // Only this fetch's own slot: an atlas from a previous life landing late
      // must not free the slot the current life's fetch holds.
      .finally(() => { if (this.heatmapInit === init) this.heatmapInit = null })
    this.heatmapInit = init
    return init
  }

  updateHeatmap(abbrCounts) {
    const heatmapData = this.normalizedHeatmapData(abbrCounts)
    if (this.chartInstance) {
      this.chartInstance.data.datasets[0].data = this.chartInstance.data.labels.map((label, index) => ({
        value: heatmapData[label] || 0,
        feature: this.chartInstance.data.datasets[0].data[index].feature
      }))
      this.chartInstance.update()
    } else {
      this.initializeHeatmap(heatmapData)
    }
  }

  destroyChart() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
      this.chartInstance = null
    }
  }

  showLoading() {
    if (!this.element.isConnected) return // torn down: never re-append the loader disconnect() removed
    if (document.getElementById("loadingDiv")) return
    const overlay = document.createElement("div")
    overlay.id = "loadingDiv"
    overlay.innerHTML = '<div class="spinner-border text-light" role="status"><span class="visually-hidden">Loading...</span></div>'
    // Inside the GOAT Store dialog (the controller rides the modal root) the
    // loader covers the dialog BODY only: a viewport-wide overlay above the
    // modal (it must sit above Bootstrap's 1050 backdrop to be seen at all)
    // would also cover the header, and the buyer could not close the dialog
    // while a request ran. The standalone page keeps the fixed overlay.
    const host = this.loadingHost()
    overlay.className = "d-flex justify-content-center align-items-center top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 " +
      (host ? "is-hosted" : "position-fixed")
    if (host) {
      host.classList.add("is-loading-host")
      host.appendChild(overlay)
    } else {
      document.body.appendChild(overlay)
    }
  }

  loadingHost() {
    return this.element.matches(".modal") ? this.element.querySelector(".gl-modal-body") : null
  }

  hideLoading() {
    this.removeLoading()
  }

  removeLoading() {
    const overlay = document.getElementById("loadingDiv")
    if (!overlay) return
    overlay.parentElement?.classList.remove("is-loading-host")
    overlay.remove()
  }

  // Route through the one canonical toast (CSS consolidation Tier 2b):
  // window.showToast builds the gl-toast card into #toast-container and the
  // gl-toast Stimulus controller drives its lifecycle. Errors linger a little
  // longer than successes, preserving the prior behaviour.
  showToast(message, variant = "success", delay = variant === "success" ? 3000 : 5000) {
    window.showToast?.(message, `bg-${variant}`, delay)
  }

  showAdminPricingWarnings(warnings) {
    if (!warnings || !Array.isArray(warnings) || warnings.length === 0) return
    // Only show warnings once per filter combination to avoid spam
    const warningKey = warnings.sort().join("|")
    if (this.lastWarningKey === warningKey) return
    this.lastWarningKey = warningKey

    warnings.forEach((warning) => {
      // Use longer delay (5 seconds) for admin warnings via global showToast
      if (typeof window.showToast === "function") {
        window.showToast(warning, "bg-warning", 5000)
      } else {
        this.showToast(warning, "warning")
      }
    })
  }

  // The page's own token (csrfTokenValue) overrides the meta-tag token
  // request.js would send, so the POSTs carry the same token as the body.
  postHeaders() {
    return {
      "X-CSRF-Token": this.csrfTokenValue,
      "Accept": "application/json",
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
    }
  }

  getSelectedGhlTags() {
    // First, try to read from hidden fields (created by inline JavaScript)
    const container = document.getElementById("aged_leads_ghl_tags_container")
    if (container) {
      const hiddenFields = container.querySelectorAll('input[name="aged_lead_order[ghl_tags][]"]')
      if (hiddenFields.length > 0) {
        return Array.from(hiddenFields).map(field => field.value).filter(Boolean)
      }
    }

    // Fall back to reading from select element
    const select = document.getElementById("aged_leads_ghl_tags_select")
    if (select) {
      return Array.from(select.selectedOptions).map(option => option.value).filter(Boolean)
    }

    return []
  }

  handleGhlTagsSelectChange() {
    // A pick is an unsaved edit: only Save writes `selectedGhlTags` (the saved
    // tags), so a pick abandoned with Close never reads as saved (BUTTON-9).
    this.syncIntegrationSave()
  }

  // =========================================================================
  // MOBILE CARDS RENDERING
  // =========================================================================

  // Canonical .gl-empty-state markup mirroring shared/_empty_state.html.haml —
  // do not hand-roll variants (audit F49); the CTA reopens the filter sheet.
  // A null ctaText renders no CTA (the server partial does the same with `cta: nil`).
  emptyStateHTML(icon, title, ctaText) {
    const cta = ctaText
      ? `<div class="gl-empty-state__cta">
            <button class="gl-btn gl-btn-outline" type="button" data-action="click->aged-lead-availability#openMobileFilters">
              <i class="ph ph-sliders-horizontal me-2"></i>
              ${ctaText}
            </button>
          </div>`
      : ""
    return `
        <div class="gl-empty-state">
          <i class="ph ${icon}" aria-hidden="true"></i>
          <p>${title}</p>
          ${cta}
        </div>
      `
  }

  renderMobileCards(states, price, ageLabel, agedBucket) {
    if (!this.mobileCards) return

    // Twin of shared/_empty_state (the server renders the same container via
    // that partial on first paint — see availability.html.haml): aria-hidden
    // icon, plain title <p>, CTA inside .gl-empty-state__cta as gl-btn-outline.
    // The CTA opens the mobile filter sheet, which the GOAT Store dialog does not
    // render (its filter strip is always in view) — there the button did nothing.
    const sheet = Boolean(this.element.querySelector("#mobile-filter-sheet"))
    if (!this.hasAllFilters()) {
      this.mobileCards.innerHTML = this.emptyStateHTML('ph-map-pin',
        'Select your lead options to see availability', sheet ? 'Select Lead Options' : null)
      return
    }

    const statesWithAvailability = states.filter(s => parseInt(s.availability, 10) > 0)

    if (statesWithAvailability.length === 0) {
      this.mobileCards.innerHTML = this.emptyStateHTML('ph-tray',
        'No leads available for the selected filters', sheet ? 'Change Filters' : null)
      return
    }

    const maxAvailMobile = statesWithAvailability.reduce((m, s) => Math.max(m, parseInt(s.availability, 10) || 0), 0)

    const cardsHtml = statesWithAvailability.map(state => {
      const max = parseInt(state.availability, 10) || 0
      const previousValue = this.sanitizeQuantity(this.stateQuantities[state.code], max)
      const disableBtn = this.purchaseBlocked() || max < 1 || !previousValue
      // Cards render only once both filters are set (statesWithAvailability),
      // so a disabled control here names its own reason rather than the hint.
      const quantityReason = max < 1 ? this.quantityDisabledReason({ filtersReady: true, availability: max }) : null
      const orderReason = disableBtn
        ? this.orderButtonDisabledReason({ filtersReady: true, availability: max, quantity: parseInt(previousValue, 10) || 0 })
        : null

      return `
        <div class="gl-availability-card" data-state="${escapeHtml(state.code)}" data-availability="${max}" data-aged-bucket="${escapeHtml(agedBucket)}" data-age-label="${escapeHtml(ageLabel)}">
          <div class="gl-availability-card-header">
            <span class="gl-availability-card-state">
              <img src="${escapeHtml(this.stateFlagUrl(state.code))}" alt="" width="18" height="18" class="me-1 align-text-bottom rounded-circle" loading="lazy">
              ${escapeHtml(state.name)}${this.unlicensedStateBadgeMarkup(state.code)}
            </span>
            <span class="gl-availability-card-price">${valueOrEmptyHtml(price)}</span>
          </div>
          <div class="gl-availability-card-body">
            <div class="gl-availability-card-stat">
              <span class="gl-availability-card-stat-value">
                ${this.availBarMarkup(max, maxAvailMobile)}
                <span class="gl-avail-count">${max}</span>
              </span>
              <span class="gl-availability-card-stat-label">Available</span>
            </div>
            <div class="gl-availability-card-qty">
              <input type="number"
                min="0"
                max="${max}"
                data-state="${escapeHtml(state.code)}"
                class="gl-filter-input gl-filter-input--numeric quantity-input"
                value="${max < 1 ? "" : escapeHtml(previousValue)}"
                ${max < 1 ? "disabled" : ""}
                ${this.disabledReasonAttrs(quantityReason)}
                placeholder="Qty"
                aria-label="Quantity for ${escapeHtml(state.name)}">
            </div>
          </div>
          <div class="gl-availability-card-actions">
            <button type="button"
              class="gl-btn gl-btn-outline gl-btn-sm order-btn"
              ${disableBtn ? "disabled" : ""}
              ${this.disabledReasonAttrs(orderReason)}
              data-state="${escapeHtml(state.code)}">
              <i class="ph-fill ph-shopping-cart me-1"></i>
              Add to Cart
            </button>
          </div>
        </div>
      `
    }).join("")

    this.mobileCards.innerHTML = cardsHtml
  }

  // =========================================================================
  // MOBILE FILTER SHEET
  // =========================================================================

  openMobileFilters() {
    if (!this.mobileFilterSheet) return

    // Sync mobile filters with desktop values
    this.syncDesktopToMobile()

    // Show overlay and sheet
    if (this.mobileFilterOverlay) this.mobileFilterOverlay.classList.add("open")
    this.mobileFilterSheet.classList.add("open")
    document.body.style.overflow = "hidden"
  }

  closeMobileFilters() {
    if (!this.mobileFilterSheet) return

    // Hide overlay and sheet
    if (this.mobileFilterOverlay) this.mobileFilterOverlay.classList.remove("open")
    this.mobileFilterSheet.classList.remove("open")
    document.body.style.overflow = ""
  }

  syncDesktopToMobile() {
    if (this.mobileLeadClassSelect && this.leadClassSelect) {
      this.mobileLeadClassSelect.value = this.leadClassSelect.value
    }
    if (this.mobileLeadQualitySelect && this.leadQualitySelect) {
      this.mobileLeadQualitySelect.value = this.leadQualitySelect.value
    }
    if (this.mobileAgeSelect && this.ageSelect) {
      this.mobileAgeSelect.value = this.ageSelect.value
      this.mobileAgeSelect.disabled = this.ageSelect.disabled
    }
  }

  syncMobileFilter(event) {
    const mobileElement = event.currentTarget
    const desktopId = mobileElement.dataset.syncDesktop

    if (desktopId) {
      const desktopElement = document.getElementById(desktopId)
      if (desktopElement) {
        desktopElement.value = mobileElement.value
        desktopElement.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    // Re-sync to update disabled states
    this.syncDesktopToMobile()
  }

  clearMobileFilters() {
    // Clear mobile selects
    if (this.mobileLeadClassSelect) this.mobileLeadClassSelect.value = ""
    if (this.mobileLeadQualitySelect) this.mobileLeadQualitySelect.value = ""
    if (this.mobileAgeSelect) {
      this.mobileAgeSelect.value = ""
      this.mobileAgeSelect.disabled = true
    }

    // Sync to desktop
    if (this.leadQualitySelect) this.leadQualitySelect.value = ""
    if (this.leadClassSelect) {
      this.leadClassSelect.value = ""
      this.leadClassSelect.dispatchEvent(new Event("change", { bubbles: true }))
    }
    if (this.ageSelect) {
      this.ageSelect.value = ""
      this.ageSelect.disabled = true
    }

    // Clear state and re-render
    this.stateQuantities = {}
    this.renderStateRows(this.buildEmptyStateRows(), null, "", "")
    this.updateHeatmap({})
  }

  applyMobileFilters() {
    this.closeMobileFilters()

    if (this.hasAllFilters()) {
      this.fetchAvailability()
    }
  }

  formatUsd(value) {
    return `$${Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  renderCartRail(rail) {
    if (this.cartRailCount) this.cartRailCount.textContent = rail.item_count
    if (this.cartRailTotal) this.cartRailTotal.textContent = this.formatUsd(rail.subtotal)

    if (this.cartRailBody) {
      if (!rail.items || rail.items.length === 0) {
        this.cartRailBody.innerHTML = `
          <div class="gl-cart-rail-empty">
            <i class="ph ph-shopping-cart" aria-hidden="true"></i>
            <p>Your cart is empty — add states to get started.</p>
          </div>`
      } else {
        this.cartRailBody.innerHTML = rail.items.map((item) => `
          <div class="gl-cart-rail-line" data-cart-item-id="${escapeHtml(item.id)}">
            <span class="gl-cart-rail-state">${escapeHtml(item.state)}</span>
            <span class="gl-cart-rail-qty">×${escapeHtml(item.quantity)}</span>
            <span class="gl-cart-rail-amount">${escapeHtml(this.formatUsd(item.amount))}</span>
            <button class="gl-cart-rail-remove" type="button" aria-label="Remove ${escapeHtml(item.state)}" data-gl-tooltip="Remove" data-gl-tooltip-pos="left" data-cart-item-id="${escapeHtml(item.id)}">
              <i class="ph ph-x" aria-hidden="true"></i>
            </button>
          </div>`).join("")
      }
    }

    if (this.integrationNotice) {
      // #13599: only once the cart meets the aged minimum (server-authoritative
      // `meets_minimum`, the same gate as the first paint) — under it the CTA
      // hint names the real blocker and the notice would compete with it.
      const showNotice = Boolean(rail.meets_minimum) && !rail.lead_integration_configured
      this.integrationNotice.classList.toggle("d-none", !showNotice)
    }

    this.agedCartTotalValue = parseInt(rail.lead_total, 10) || 0
    this.updateCartActionButtons()
  }

  handleCartRailClick(event) {
    const removeBtn = event.target.closest(".gl-cart-rail-remove")
    if (!removeBtn) return
    event.preventDefault()
    this.removeCartItem(removeBtn.dataset.cartItemId)
  }

  // @rails/request.js carries the CSRF token and same-origin credentials itself.
  async removeCartItem(id) {
    const lifecycle = this.lifecycle
    this.showLoading()
    try {
      const response = await destroy(`/cart_items/${id}`, { responseKind: "json" })
      const rail = await this.ensureJson(response, REMOVE_ITEM_FALLBACK_MESSAGE)
      if (!this.current(lifecycle)) return
      this.hideLoading()
      this.renderCartRail(rail)
      this.fetchAvailability()
    } catch (error) {
      if (!this.current(lifecycle)) return
      this.hideLoading()
      if (error?.data?.cart_lock) {
        this.showToast(error.message, "warning", CHECKOUT_LOCK_TOAST_MS)
        return
      }
      console.error("Remove cart item error", error)
      this.showToast(error?.message || REMOVE_ITEM_FALLBACK_MESSAGE, "danger")
    }
  }

  // Resolves the JSON body or throws an Error carrying the server's sentence
  // (`errors` as an array, a string or a field map). A response that is not
  // JSON — a CSRF 422 page, a 500 page — throws `fallback`, never
  // response.statusText or the raw body. Accepts a request.js FetchResponse
  // (`json` is a promise) and a native Response (`json()` is a method).
  async ensureJson(response, fallback = UNEXPECTED_RESPONSE_MESSAGE) {
    const data = await this.responseJson(response)
    if (response.ok && data !== null) return data

    // `errors` (string / array / ActiveModel hash) is the usual shape; the aged
    // CSRF handler and the sign-in 401 answer with a singular `error` sentence.
    const errors = data?.errors ?? data?.error
    const message = Array.isArray(errors) ? errors.join(", ")
      : typeof errors === "string" ? errors
        : errors && typeof errors === "object" ? Object.values(errors).flat().join(", ")
          : ""
    const error = new Error(message || fallback)
    error.data = data
    throw error
  }

  // Read `json` exactly once: on a FetchResponse the getter mints the promise
  // (a rejected one for a non-JSON body), so probing it with typeof and then
  // reading it again would leave an unhandled rejection behind.
  async responseJson(response) {
    try {
      const json = response.json
      return typeof json === "function" ? await json.call(response) : await json
    } catch {
      return null
    }
  }
}
